import { create } from "zustand";
import {
  createWorkflowRunCoreSlice,
  workflowRunCoreInitialState,
  type WorkflowRunCore,
  type WorkflowInputFile,
} from "src/views/products/workflow/store/createWorkflowRunStore";
import type { StageResponse } from "../services/ProposalGenerationServices";

// PG's historical name for a workflow input file, kept so existing imports
// stay valid.
export type RfpFileState = WorkflowInputFile;

export interface ReportTemplate {
  id: number;
  name: string;
  description?: string;
  artifact_fk?: number;
  dataset_fk?: number | null;
}

export interface IdentifiedStages {
  stages: string[];
  reviewReportSdrId: number;
}

type ExtrasState = {
  selectedTemplate: ReportTemplate | null;
  reportSdrId: number | null;
  stages: StageResponse[];
  stageReportPrefix: string | null;
  stageReportSuffixLabel: string | null;
  identifiedStages: IdentifiedStages | null;
  showStageApprovalModal: boolean;
  reportType: string | null;
  sequenceNumber: number | null;
};

type ExtrasAction = {
  setSelectedTemplate: (template: ReportTemplate | null) => void;
  setReportSdrId: (id: number | null) => void;
  setStages: (stages: StageResponse[]) => void;
  setStageReportPrefix: (prefix: string | null) => void;
  setStageReportSuffixLabel: (suffix: string | null) => void;
  setIdentifiedStages: (data: IdentifiedStages | null) => void;
  setShowStageApprovalModal: (show: boolean) => void;
  setReportType: (reportType: string | null) => void;
  setSequenceNumber: (sequenceNumber: number | null) => void;
  reset: () => void;
};

const extrasInitialState: ExtrasState = {
  selectedTemplate: null,
  reportSdrId: null,
  stages: [],
  stageReportPrefix: null,
  stageReportSuffixLabel: null,
  identifiedStages: null,
  showStageApprovalModal: false,
  reportType: "review report",
  sequenceNumber: 1,
};

export const useProposalGenerationStore = create<
  WorkflowRunCore & ExtrasState & ExtrasAction
>()((set, get) => ({
  ...createWorkflowRunCoreSlice(set, get),
  ...extrasInitialState,
  setSelectedTemplate: (template) => set({ selectedTemplate: template }),
  setReportSdrId: (id) => set({ reportSdrId: id }),
  setStages: (stages) => set({ stages }),
  setStageReportPrefix: (prefix) => set({ stageReportPrefix: prefix }),
  setStageReportSuffixLabel: (suffix) =>
    set({ stageReportSuffixLabel: suffix }),
  setIdentifiedStages: (data) => set({ identifiedStages: data }),
  setShowStageApprovalModal: (show) => set({ showStageApprovalModal: show }),
  setReportType: (reportType) => set({ reportType }),
  setSequenceNumber: (sequenceNumber) => set({ sequenceNumber }),
  reset: () =>
    set((state) => ({
      ...workflowRunCoreInitialState,
      ...extrasInitialState,
      // See createWorkflowRunCoreSlice: the uploader's registered function
      // must survive data resets.
      removeUploadedFile: state.removeUploadedFile,
    })),
}));
